/**
 * The admin's path from an empty board to kickoff.
 *
 * A brand-new game is a blank 10x10 grid with hidden numbers and no players,
 * which tells a first-time admin nothing about what to do next. This sits above
 * the board until every step is done, then disappears for good.
 *
 * Shown only to the admin: a player looking at someone else's board cannot act
 * on any of it.
 */

import { setupChecklist } from '../../shared/game-logic';
import type { GameState } from '../../shared/types';

interface Props {
  state: GameState;
  /** Jumps to the Manage tab, where every step actually happens. */
  onManage: () => void;
}

export function SetupChecklist({ state, onManage }: Props) {
  const steps = setupChecklist(state);
  const done = steps.filter((s) => s.done).length;
  if (done === steps.length) return null;

  // The first unfinished step is the one to do next; the rest stay quiet.
  const nextIndex = steps.findIndex((s) => !s.done);

  return (
    <section className="card checklist" aria-label="Set this game up">
      <div className="row" style={{ marginBottom: 10 }}>
        <h2 className="card-title" style={{ margin: 0 }}>
          Set this game up
        </h2>
        <div className="spacer" />
        <span className="small muted">
          {done} of {steps.length}
        </span>
      </div>

      <ol className="checklist-steps">
        {steps.map((step, i) => (
          <li
            key={step.id}
            className={step.done ? 'done' : i === nextIndex ? 'next' : ''}
          >
            <span className="checklist-mark" aria-hidden="true">
              {step.done ? '✓' : i + 1}
            </span>
            <span style={{ minWidth: 0 }}>
              <strong>{step.label}</strong>
              {i === nextIndex && (
                <span className="small muted" style={{ display: 'block' }}>
                  {step.hint}
                </span>
              )}
            </span>
          </li>
        ))}
      </ol>

      <button
        type="button"
        className="btn-primary"
        style={{ width: '100%', marginTop: 12 }}
        onClick={onManage}
      >
        {nextIndex === 0 ? 'Get started' : 'Continue in Manage'}
      </button>
    </section>
  );
}
